// TodaySchedule.js
import axios from "axios";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState, useMemo } from "react";
import { FaArchive, FaCalendarDay, FaCopy, FaEdit, FaExclamationCircle, FaShareAlt, FaVideo } from "react-icons/fa";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import CompanySelector from "ATSComponents/Common/CompanySelector";
import SuccessErrorPopup from "ATSComponents/Common/SuccessErrorPopup";
import { Calendar } from "primereact/calendar";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Col, Row } from "reactstrap";
import { ProgressSpinner } from "primereact/progressspinner";
import Select, { components } from "react-select";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import { getCandidateNames } from "./calendarCandidateUtils";
import Swal from "sweetalert2";
import { getCalendarApiErrorMessage } from "./calendarError";

const MultiValueLabelWithTitle = props => (
  <components.MultiValueLabel {...props}>
    <span title={props.selectProps.selectedNamesTitle || props.data?.displayName || props.data?.name || ""}>{props.children}</span>
  </components.MultiValueLabel>
)

const SelectControlWithSelectedNamesTitle = props => (
  <components.Control
    {...props}
    innerProps={{
      ...props.innerProps,
      title: props.selectProps.selectedNamesTitle,
    }}
  />
)

const isAllCalendarCompany = companyId => String(companyId || "").toLowerCase() === "all"
const blockAllCompanyMutation = () => {
  Swal.fire({
    icon: "warning",
    title: "Select a company",
    text: "Please select a specific company before editing, archiving, or deleting a calendar event.",
    confirmButtonText: "OK",
  })
}

/**
 * TodaySchedule.js (Optimized)
 *
 * - Inlined LinkContactsPopup and LinkCandidate as local components
 * - Kept the same dialogs / UX, simplified state and removed unused code
 * - Uses companyId from parent component (TodaySchedule)
 *
 * NOTE: This file replaces reusable components with inline equivalents,
 * preserving dialog look/behavior and optimizing logic.
 */

const TodaySchedule = () => {
  document.title = "ATS - Today's Schedule";
  // ====== STATE ======
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedEventForShare, setSelectedEventForShare] = useState(null);
  const [shareRecruiters, setShareRecruiters] = useState([]);
  const [shareCandidates, setShareCandidates] = useState([]);
  const [shareRecruiterOptions, setShareRecruiterOptions] = useState([]);
  const [isSharing, setIsSharing] = useState(false);

  const handleCopyMeetingLink = async link => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch (error) {
      const tempInput = document.createElement("input");
      tempInput.value = link;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
    }
  };

  useEffect(() => {
    if (!showShareModal || !companyId) return;
    const fetchRecruiters = async () => {
      try {
        const response = await axios.get(`/api/v1/get-all-recruiters/?company_id=${companyId}`);
        const data = response.data?.results || response.data || [];
        setShareRecruiterOptions(
          data.map(contact => ({
            id: contact.id,
            name: [contact.first_name, contact.middle_name, contact.last_name].filter(Boolean).join(" ") || contact.email || `User ${contact.id}`,
          }))
        );
      } catch (error) {
        setShareRecruiterOptions([]);
      }
    };
    fetchRecruiters();
  }, [showShareModal, companyId]);

  const handleOpenShareModal = event => {
    setSelectedEventForShare(event);
    setShareRecruiters([]);
    setShareCandidates([]);
    setShowShareModal(true);
  };

  const handleSubmitShareMeeting = async () => {
    if (!selectedEventForShare) return;
    if (!shareRecruiters.length && !shareCandidates.length) {
      alert("Please select at least one recruiter or candidate to share with.");
      return;
    }
    setIsSharing(true);
    try {
      await axios.post("/api/v1/meeting-invite/", {
        candidate_ids: shareCandidates,
        employee_ids: shareRecruiters,
        calendar_id: selectedEventForShare.calendar_id,
      });
      setShowShareModal(false);
    } catch (error) {
      alert("Failed to share meeting link. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  const renderMeetingLinkActions = (link, event) => {
    if (!link) return "N/A";
    return (
      <div className="d-inline-flex align-items-center gap-1">
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#1a5fa8",
            background: "#eef6ff",
            border: "1px solid #d4e7fb",
            borderRadius: 6,
            width: 32,
            height: 32,
            padding: 0,
            fontWeight: 700,
            textDecoration: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
          title="Join Meeting"
        >
          <FaVideo />
        </a>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          title="Copy Meeting Link"
          onClick={() => handleCopyMeetingLink(link)}
        >
          <FaCopy style={{ fontSize: 12 }} />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          title="Share Meeting Link"
          onClick={() => handleOpenShareModal(event)}
        >
          <FaShareAlt style={{ fontSize: 12 }} />
        </button>
      </div>
    );
  };

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveReasonErr, setArchiveReasonErr] = useState("");
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [calendarIdToArchive, setCalendarIdToArchive] = useState(null);

  // Success / Error Popup
  const [errorMessage, setErrorMessage] = useState("");
  const [errorAlert, setErrorAlert] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleCloseErrorModal = () => setErrorAlert(false);
  const [scheduleDuration, setScheduleDuration] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedContactsLocal, setSelectedContactsLocal] = useState([]);
  const [parentContacts, setParentContacts] = useState([]);

  const dt = useRef(null);

  // ====== FILTER SETUP ======
  const [filters, setFilters] = useState({
    subject: { value: null, matchMode: FilterMatchMode.CONTAINS },
    created_by_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    event_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
    candidate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    job: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.CONTAINS },
    start_date: { value: null, matchMode: FilterMatchMode.EQUALS },
    start_time: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  // ====== FETCH DATA ======
  const userInfos = JSON.parse(sessionStorage.getItem("user-info"));
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id;

  useEffect(() => {
    if (initialCompanyId) setCompanyId(initialCompanyId);
  }, [initialCompanyId]);

  useEffect(() => {
    if (companyId) fetchCalendars();
  }, [companyId]);

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `/api/v1/events/?today_events=true&company_id=${companyId}`
      );
      const results = res.data.results || [];
      setData(results);
      setFilteredData(results);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (company) => {
    if (company?.company_id) {
      setCompanyId(company.company_id);
    }
  };

  // ====== FILTERING ======
  const onFilter = (event) => {
    const { filters: newFilters } = event;
    setFilters(newFilters);

    const filtered = data.filter((item) =>
      Object.keys(newFilters).every((key) => {
        const filterValue = newFilters[key]?.value;
        if (!filterValue) return true;

        let fieldValue = "";
        switch (key) {
          case "candidate":
            fieldValue = getCandidateNames(item.candidate_ids);
            break;
          case "job":
            fieldValue = item.job_ids?.[0]?.name || "";
            break;
          default:
            fieldValue = item[key] || "";
        }

        return fieldValue
          .toString()
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      })
    );

    setFilteredData(filtered);
    setFirst(0);
  };

  // ====== PAGINATION ======
  const paginatedData = filteredData.slice(first, first + rows);

  // ====== ACTION HANDLERS ======
  const handleArchive = (row) => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    setCalendarIdToArchive(row.calendar_id);
    setShowArchiveModal(true);
  };

  const handleSaveArchive = async () => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    setArchiveSaving(true);
    setArchiveReasonErr("");

    if (!archiveReason.trim()) {
      setArchiveReasonErr("Please provide a reason for archiving.");
      setArchiveSaving(false);
      return;
    }

    const payload = {
      reason: archiveReason,
      calendar_id: calendarIdToArchive,
    };

    try {
      await axios.post(`/api/v1/events/archive/`, payload);
      setIsSuccess(true);
      setErrorMessage("Calendar archived successfully.");
      setErrorAlert(true);
      setShowArchiveModal(false);
      setArchiveReason("");
      fetchCalendars(); // refresh after success
    } catch (error) {
      setIsSuccess(false);
      const message = getCalendarApiErrorMessage(error, "Failed to archive calendar.");
      setErrorMessage(message);
      setErrorAlert(true);
      Swal.fire({ icon: "error", title: "Archive failed", text: message, confirmButtonText: "OK" });
    } finally {
      setArchiveSaving(false);
    }
  };

  // ====== RENDER ACTION ICONS ======
  const actionTemplate = (rowData) => (
    <div className="d-flex justify-content-center gap-3">
      <FaEdit
        className="text-primary"
        size={18}
        title="Edit"
        style={{ cursor: "pointer" }}
        onClick={() => handleEdit(rowData)}
      />
      <FaArchive
        className="text-danger"
        size={18}
        title="Archive"
        style={{ cursor: "pointer" }}
        onClick={() => handleArchive(rowData)}
      />
    </div>
  );

  // ========== EDIT / SCHEDULE DIALOG STATE ==========
  const [selectedRow, setSelectedRow] = useState(null);
  const [scheduleCommonPopup, setScheduleCommonPopup] = useState(false);
  const [IdforEdit, setIdForEdit] = useState(null);
  const closeSchedulePopup = () => {
    setScheduleCommonPopup(false);
    setIdForEdit(null);
    setSelectedRow(null);
  };

  // interview types / subtypes
  const [scheduleTypeId, setScheduleTypeId] = useState(null);
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [subtypeCall, setSubTypeCall] = useState([]);

  const fetchInterviewTypes = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-schedule-type/?interview=true");

      if (response.status === 200) {
        const transformedData = response.data.results.map((type) => ({
          name: type.schedule_type,
          code: type.id,
        }));
        setInterviewTypes(transformedData);
        const interviewType = transformedData.find((t) => t.name === "Interview");
        if (interviewType) {
          setScheduleTypeId(interviewType.code);
        }
      }
    } catch (error) {
      console.error("Error fetching interview types:", error);
    }
  };

  const fetchSubTypes = async (id) => {
    if (!id) return;

    try {
      const response = await axios.get(`/api/v1/get-all-schedule-sub-type/?schedule_id=${id}`);
      const scheduleData = response.data?.results || [];
      const transformedSubTypes = scheduleData.map((schedule) => ({
        name: schedule.schedule_subtype || "Unnamed Sub-Type",
        code: schedule.id || "",
      }));
      setSubTypeCall(transformedSubTypes);
    } catch (error) {
      console.error("Error fetching sub-types:", error);
    }
  };

  useEffect(() => {
    fetchInterviewTypes();
  }, []);

  useEffect(() => {
    if (scheduleTypeId) {
      fetchSubTypes(scheduleTypeId);
    }
  }, [scheduleTypeId]);

  const priorityValue = [
    { name: "Low", value: "low" },
    { name: "Medium", value: "medium" },
    { name: "High", value: "high" },
  ];
  const [loadingSave, setLoadingSave] = useState(false);

  // schedule fields
  const [scheduleSubtype, setScheduleSubtype] = useState(null);
  const [scheduleStartDate, setScheduleStartdate] = useState(null);
  const [scheduleStartTime, setScheduleStarttime] = useState(null);
  const [scheduleEndDate, setScheduleEndDate] = useState(null);
  const [scheduleEndTime, setScheduleEndTime] = useState(null);
  const [scheduleReminder, setScheduleReminder] = useState(null);
  const [scheduleRepeat, setScheduleRepeat] = useState(null);
  const [scheduleAutoFollowup, setScheduleAutoFollowup] = useState(null);
  const [schedulePriority, setSchedulePriority] = useState(null);

  const reminderOptions2 = [
    { label: "None", value: "" },
    { label: "5 min", value: 5 },
    { label: "10 min", value: 10 },
    { label: "15 min", value: 15 },
    { label: "30 min", value: 30 },
    { label: "1 hour", value: 60 },
  ];
  const [scheduleSubject, setScheduleSubject] = useState("");
  const [popTextares, setPopTextares] = useState("");

  const [popchecked, setPopchecked] = useState(false);
  const [schedulepopchecked2, setSchedulepopchecked2] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const [userid, setUserid] = useState([]); // existing user ids for schedule
  const [selectedScheduleJobs, setSelectedScheduleJobs] = useState([]); // [{id,name}]
  const [selectedScheduleCandidates, setSelectedScheduleCandidates] = useState([]); // [{id,name}]
  const [selectedScheduleContacts, setSelectedScheduleContacts] = useState([]); // [{id,name}]
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [userid2, setUserid2] = useState([]);

  const [scheduleFormErrors, setScheduleFormErrors] = useState({});

  // ========== HANDLERS: Edit & Save ==========
  const handleEdit = async (row) => {
    if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
    console.log(row, "row")
    const data = row;
    try {
      setLoading(true);
      setIdForEdit(row.calendar_id);
      setScheduleFormErrors({});
      setSelectedRow(row);

      // FETCH FULL EVENT DETAILS
      const res = await axios.get(
        `/api/v1/events/${row.calendar_id}/?company_id=${companyId}`
      );

      const ev = res.data;

      // Fill popup fields
      const subtypeObj =
        subtypeCall.find((s) => s.name === ev.event_sub_type || s.code === ev.event_sub_type) || {
          name: ev.event_sub_type,
        };
      setScheduleSubtype(subtypeObj);
      setScheduleSubject(ev.subject || "");
      setPopTextares(ev.description || "");

      setScheduleStartdate(ev.start_date ? new Date(ev.start_date) : null);
      setScheduleEndDate(ev.end_date ? new Date(ev.end_date) : null);

      setScheduleStarttime(ev.start_time ? new Date(`1970-01-01T${ev.start_time}`) : null);
      setScheduleEndTime(ev.end_time ? new Date(`1970-01-01T${ev.end_time}`) : null);

      setScheduleReminder(ev.reminder ? { value: ev.reminder } : null);
      setScheduleRepeat(ev.repeat ? { value: ev.repeat } : null);
      setScheduleAutoFollowup(ev.auto_followup ? { value: ev.auto_followup } : null);
      setSchedulePriority(data.event_priority);


      let durationValue = null;

      if (data.duration) {
        durationValue = parseInt(data.duration); // Extract 45 from "45 Minutes"
      }

      setScheduleDuration(durationValue);
      // setScheduleDuration(data.duration);

      setPopchecked(Boolean(ev.completed));
      setSchedulepopchecked2(Boolean(ev.private));

      setUserid(ev.user_ids || []);

      // Jobs / Candidates / Contacts - normalize to {id, name}
      setSelectedScheduleJobs(data.job_ids?.map((j) => ({ id: j.id, name: j.name })) || []);
      setSelectedScheduleCandidates(data.candidate_ids?.map((c) => ({ id: c.id, name: c.name })) || []);
      setSelectedScheduleContacts(data.contact_ids?.map((ct) => ({ id: ct.id, name: ct.name })) || []);

      const selected = row.contact_ids || [];

      setSelectedEmployees(selected);               // MultiSelect prefill
      setSelectedContactsLocal(selected);           // Dialog DataTable prefill
      setParentContacts(selected.map(e => e.id));
      // setSelectedEmployees(data.contact_ids?.map((ct) => ({ id: ct.id, name: ct.name })) || [])

      setScheduleCommonPopup(true);
    } catch (err) {
      console.error("Error loading event details:", err);
      setErrorMessage("Failed to load event details.");
      setIsSuccess(false);
      setErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };
const formatTime = (timeString) => {
  if (!timeString || typeof timeString !== "string") return "-";
  const parts = timeString.split(":");
  if (parts.length < 2) return "-";
  const [hours, minutes] = parts;
  if (Number.isNaN(Number(hours)) || Number.isNaN(Number(minutes))) return "-";
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};
const formatInterviewDate = (dateString) => {
  if (!dateString) return "-";
  const [year, month, day] = String(dateString).split("T")[0].split("-");
  if (!year || !month || !day) return "-";
  return `${day}-${month}-${year}`;
};
const handleSaveSchedule = async () => {
  if (isAllCalendarCompany(companyId)) return blockAllCompanyMutation()
  const row = selectedRow || {};



    const payload = {
      event_type: row.event_type || "Interview",
      event_sub_type: scheduleSubtype?.name || row.event_sub_type,
      subject: scheduleSubject || row.subject || "",
      description: popTextares ?? row.description ?? "",

      start_date: scheduleStartDate
        ? new Date(scheduleStartDate).toISOString().split("T")[0]
        : row.start_date,

      start_time: scheduleStartTime
        ? new Date(scheduleStartTime).toLocaleTimeString("en-GB", { hour12: false })
        : row.start_time,

      end_date: scheduleEndDate
        ? new Date(scheduleEndDate).toISOString().split("T")[0]
        : row.end_date,

      end_time: scheduleEndTime
        ? new Date(scheduleEndTime).toLocaleTimeString("en-GB", { hour12: false })
        : row.end_time,

      reminder: scheduleReminder?.value || row.reminder || "10",
      repeat: scheduleRepeat?.value || row.repeat || null,
      auto_followup: scheduleAutoFollowup?.value || row.auto_followup || null,

      completed: typeof popchecked === "boolean" ? popchecked : row.completed ?? false,
      private: typeof schedulepopchecked2 === "boolean" ? schedulepopchecked2 : row.private ?? false,

      user_ids: userid?.length ? userid : row.user_ids || [],

      job_id: selectedScheduleJobs?.length
        ? selectedScheduleJobs.map((j) => j.id)
        : row.job_ids?.map((j) => j.id) || [],

      candidate_id: selectedScheduleCandidates?.length
        ? selectedScheduleCandidates.map((c) => c.id)
        : row.candidate_ids?.map((c) => c.id) || [],

     contact_id: selectedScheduleContacts?.length
  ? selectedScheduleContacts.map(c => (typeof c === "object" ? c.id : c))
  : row.contact_ids?.map(c => (typeof c === "object" ? c.id : c)) || [],

        
      event_priority: schedulePriority || row.event_priority || null, 
      duration: scheduleDuration ? `${scheduleDuration} Minutes` : row.duration || null,
    };

    console.log("➡️ Final Payload:", selectedScheduleContacts,row.contact_ids);

    try {
      setLoadingSave(true);

      if (!row.calendar_id) {
        throw new Error("No calendar_id found for update");
      }

      await axios.put(
        `/api/v1/events/${row.calendar_id}/?is_update_flow=true&company_id=${companyId}`,
        payload
      );

      closeSchedulePopup();
      setErrorAlert(true);
      setIsSuccess(true);
      setErrorMessage("Schedule updated successfully.");
      // refresh list
      fetchCalendars();
    } catch (error) {
      console.error("❌ Error updating schedule:", error);
      setErrorAlert(true);
      setIsSuccess(false);
      const message = getCalendarApiErrorMessage(error, "Failed to update schedule.");
      setErrorMessage(message);
      Swal.fire({ icon: "error", title: "Update failed", text: message, confirmButtonText: "OK" });
    } finally {
      setLoadingSave(false);
    }
  };

  // ========== Inline Components (replacing reusable ones) ==========
  // Both are defined inside TodaySchedule so they can access companyId and parent setters easily.

  /**
   * InlineLinkContacts
   *
   * Responsible for:
   *  - Job picker dialog (single selection)
   *  - Employees MultiSelect (recruiters / hiring managers)
   *  - Optional contact selection dialog (multiple)
   */
  const InlineLinkContacts = ({
    onSelectedJobsChange,
    errorJob,
    setSelectedScheduleContacts: setParentContacts,
    errorContact,
    jobLabel,
    selectedJobs,
  }) => {
    // job dialog
    const [jobDetailsVisible, setJobDetailsVisible] = useState(false);
    const [searchTextJobs, setSearchTextJobs] = useState("");
    const [selectedJobLocal, setSelectedJobLocal] = useState(
      selectedJobs?.[0] || null
    );
    const [lazyParamsJobs, setLazyParamsJobs] = useState({ page: 0, rows: 10 });
    const [jobs, setJobs] = useState([]);
    const [totalRecordsJobs, setTotalRecordsJobs] = useState(0);
    const [loadingJobs, setLoadingJobs] = useState(false);

    // employees multiselect
    const [priorityContactOptions, setPriorityContactOptions] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(true);


    // contact dialog (multi)
    const [dialogVisible, setDialogVisible] = useState(false);


    // fetch jobs (active approved/open)
    const getAllActiveJobs = async () => {
      setLoadingJobs(true);
      try {
        const page = lazyParamsJobs?.page ?? 0;
        const rows = lazyParamsJobs?.rows ?? 10;
        const response = await axios.get(
          `/api/v1/jobs/?page=${page + 1}&size=${rows}&status=Approved / Open&company_id=${companyId}`
        );
        if (response.status === 200) {
          const transformedData = response.data.results.map((cond) => ({
            title: cond.job_title,
            id: cond.job_id,
            manager: cond.hiring_manager_name,
            job_number: cond.job_number,
            name: cond.job_title,
          }));
          setJobs(transformedData);
          setTotalRecordsJobs(response.data.count || 0);
        } else {
          setJobs([]);
          setTotalRecordsJobs(0);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobs([]);
        setTotalRecordsJobs(0);
      } finally {
        setLoadingJobs(false);
      }
    };

    useEffect(() => {
      getAllActiveJobs();

    }, [lazyParamsJobs.page, lazyParamsJobs.rows, companyId]);

    // fetch contacts / recruiters for company
    const getAllActiveContacts = async () => {
      setLoadingContacts(true);
      try {
        const response = await axios.get(
          `/api/v1/get-all-recruiters/?company_id=${companyId}`
        );
        // response.data assumed to be array of users
        const transformedData = (response.data || []).map((contact) => ({
          // name: contact?.first_name || contact?.name || "Unnamed",
          name: `${contact?.first_name} ${contact?.middle_name} ${contact?.last_name}`,
          id: contact?.id,
        }));
        setPriorityContactOptions(transformedData);
      } catch (error) {
        console.error("Error fetching contacts:", error);
        setPriorityContactOptions([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    useEffect(() => {
      if (companyId) getAllActiveContacts();

    }, [companyId]);

    const filteredJobs = (dataList) =>
      dataList.filter(
        (job) =>
          (job.title || "")
            .toString()
            .toLowerCase()
            .includes(searchTextJobs.toLowerCase()) ||
          (job.manager || "")
            .toString()
            .toLowerCase()
            .includes(searchTextJobs.toLowerCase()) ||
          (job.job_number || "")
            .toString()
            .toLowerCase()
            .includes(searchTextJobs.toLowerCase()) ||
          (job.id || "").toString().includes(searchTextJobs)
      );

    const onPageChangeJobs = (event) => {
      setLazyParamsJobs({ page: event.page, rows: event.rows });
    };

    const handleSaveJob = () => {
      setJobDetailsVisible(false);
      onSelectedJobsChange?.(selectedJobLocal ? [selectedJobLocal] : []);
    };

    const handleRefreshJobs = () => {
      setSelectedJobLocal(null);
      setSearchTextJobs("");
      setLazyParamsJobs({ page: 0, rows: lazyParamsJobs.rows });
    };

    const handleOpenContactDialog = () => {
      setDialogVisible(true);
    };
    const closeDialogContact = () => {
      setDialogVisible(false);
    };

    const handleSaveContact = () => {
      const selectedIds = selectedContactsLocal.map((c) => c.id);
      // set priority contact string locally if needed
      setParentContacts(selectedIds);
      setDialogVisible(false);
    };

    return (
      <>
        <Col lg={6}>
          <div className="field">
            <label htmlFor="job" className="block">
              {jobLabel || <span>Jobs</span>}
            </label>
            <div
              className="d-flex align-items-center"
              style={{ position: "relative" }}
            >
              <InputText
                id="job"
                value={
                  selectedJobLocal
                    ? `${selectedJobLocal.title || selectedJobLocal.name}, ${selectedJobLocal.job_number || selectedJobLocal.id
                    }`
                    : selectedJobs?.[0]?.name || ""
                }
                placeholder="Select Job"
                className="w-full"
                onClick={() => setJobDetailsVisible(true)}
                style={{ paddingRight: "2rem", cursor: "pointer" }}
                readOnly
              />
              <i
                className="pi pi-chevron-down"
                onClick={() => setJobDetailsVisible(true)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                }}
              ></i>
            </div>

            <Dialog
              header="Link Jobs"
              visible={jobDetailsVisible}
              onHide={() => setJobDetailsVisible(false)}
              style={{ width: "40vw" }}
              breakpoints={{ "960px": "75vw", "641px": "100vw" }}
              className="cand-details"
            >
              <Row>
                <Col lg={12}>
                  <div
                    className="p-inputgroup mb-3"
                    style={{ position: "relative" }}
                  >
                    <InputText
                      placeholder="Search Jobs"
                      value={searchTextJobs}
                      onInput={(e) => setSearchTextJobs(e.target.value)}
                      style={{ paddingRight: "2rem" }}
                    />
                    <i
                      className="pi pi-search"
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "49%",
                        transform: "translateY(-50%)",
                      }}
                    ></i>
                  </div>
                </Col>
              </Row>

              <DataTable
                value={filteredJobs(jobs)}
                selection={selectedJobLocal}
                onSelectionChange={(e) => setSelectedJobLocal(e.value)}
                dataKey="id"
                size="small"
                lazy
                paginator
                first={lazyParamsJobs.page * lazyParamsJobs.rows}
                rows={lazyParamsJobs.rows}
                totalRecords={totalRecordsJobs}
                onPage={onPageChangeJobs}
                loading={loadingJobs}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                rowsPerPageOptions={[10, 20, 50, totalRecordsJobs]}
                id="JobTableView"
              >
                <Column selectionMode="single" style={{ width: "3em" }} />
                <Column field="job_number" header="Job ID" />
                <Column field="title" header="Job Title" />
                <Column field="handled_by" header="Contact" />
              </DataTable>

              <div className="d-flex mt-3 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary me-2 btn-main"
                  onClick={handleSaveJob}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-main"
                  onClick={handleRefreshJobs}
                >
                  Refresh
                </button>
              </div>
            </Dialog>

            {errorJob && <p style={{ color: "red" }}>{errorJob}</p>}
          </div>
        </Col>

        <Col xl={6}>
          <div className="p-field flex flex-column">
            <label htmlFor="contact" className="block">
              Interviewer
            </label>
            <div
              className="d-flex align-items-center"
              style={{ position: "relative" }}
            >
              <Select
                id="company"
                value={selectedEmployees}
                options={priorityContactOptions}
                onChange={(value) => {
                  const nextSelectedEmployees = value || [];
                  setSelectedEmployees(nextSelectedEmployees);
                  const selectedIds = nextSelectedEmployees.map((item) => item.id);
                  setParentContacts(selectedIds);
                }}
                placeholder="Select Interviewer"
                getOptionLabel={option => option.name}
                getOptionValue={option => String(option.id)}
                isMulti
                isSearchable
                isClearable
                isLoading={loadingContacts}
                closeMenuOnSelect={false}
                className="w-full"
                classNamePrefix="interviewer-select"
                components={{
                  Control: SelectControlWithSelectedNamesTitle,
                  MultiValueLabel: MultiValueLabelWithTitle,
                }}
                selectedNamesTitle={(selectedEmployees || []).map(emp => emp.name || "--").join(", ") || "Select Interviewer"}
              />
            </div>
            {errorContact && <p style={{ color: "red" }}>{errorContact}</p>}

            <Dialog
              header="Select Contacts"
              visible={dialogVisible}
              onHide={closeDialogContact}
              style={{ width: "40vw" }}
              breakpoints={{ "960px": "75vw", "641px": "100vw" }}
              className="cand-details"
            >
              <Col lg={12}>
                <div
                  className="p-inputgroup mb-3"
                  style={{ position: "relative" }}
                >
                  <InputText
                    placeholder="Search Contacts"
                    value={""}
                    onInput={() => { }}
                    style={{ paddingRight: "2rem" }}
                  />
                  <i
                    className="pi pi-search"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "49%",
                      transform: "translateY(-50%)",
                    }}
                  ></i>
                </div>
              </Col>

              <DataTable
                value={priorityContactOptions}
                paginator
                rows={5}
                selection={selectedContactsLocal}
                onSelectionChange={(e) => setSelectedContactsLocal(e.value)}
                dataKey="id"
                rowsPerPageOptions={[5, 10, 25]}
                size="small"
              >
                <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
                <Column field="name" header="Contact Name" />
              </DataTable>

              <div className="d-flex mt-3">
                <button
                  type="button"
                  className="btn btn-primary me-2 btn-main"
                  onClick={handleSaveContact}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-main"
                  onClick={closeDialogContact}
                >
                  Cancel
                </button>
              </div>
            </Dialog>
          </div>
        </Col>
      </>
    );
  }; // End InlineLinkContacts

  /**
   * InlineLinkCandidate
   *
   * Responsible for:
   *  - Candidate picker dialog (single selection or immediate selection)
   *  - Fetch candidates for selected jobId
   *  - Talent scan modal view
   */
  const InlineLinkCandidate = ({
    onSelectedCandidatesChange,
    error,
    jobId,
    label,
    selectedCandidates,
  }) => {
    const [jobDetailsVisible, setJobDetailsVisible] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedCandidateLocal, setSelectedCandidateLocal] = useState(
      selectedCandidates?.[0] || null
    );
    const [lazyParams, setLazyParams] = useState({ page: 0, rows: 10 });
    const [jobsCandidates, setJobsCandidates] = useState([]);
    const [totalRecordsCandidates, setTotalRecordsCandidates] = useState(0);
    const [loadingCandidates, setLoadingCandidates] = useState(false);

    // talent scan
    const [talentScanResults, setTalentScanResults] = useState([]);
    const [showTalentScanModal, setShowTalentScanModal] = useState(false);
    const [loadingTalentScanId, setLoadingTalentScanId] = useState(null);

    const getCandidatesForJob = async () => {
      if (!jobId) {
        setJobsCandidates([]);
        setTotalRecordsCandidates(0);
        return;
      }

      setLoadingCandidates(true);
      try {
        const page = lazyParams?.page ?? 0;
        const rowsLocal = lazyParams?.rows ?? 10;
        const response = await axios.get(
          `/api/v1/candidate-jobs/?page=${page + 1}&size=${rowsLocal}&job_id=${jobId}&company_id=${companyId}`
        );
        if (response.status === 200) {
          const transformedData = response.data.results.map((candidate) => ({
            first_name: candidate.name,
            designation: candidate.designation_name,
            current_company: candidate.current_company_name,
            email: candidate.email,
            id: candidate.candidate_id,
            talent_scan_id: candidate.talent_scan_id,
          }));
          setJobsCandidates(transformedData);
          setTotalRecordsCandidates(response.data.count || 0);
        } else {
          setJobsCandidates([]);
          setTotalRecordsCandidates(0);
        }
      } catch (error) {
        console.error("Error fetching candidates for job:", error);
        setJobsCandidates([]);
        setTotalRecordsCandidates(0);
      } finally {
        setLoadingCandidates(false);
      }
    };

    useEffect(() => {
      getCandidatesForJob();

    }, [jobId, lazyParams.page, lazyParams.rows, companyId]);

    const filteredJobs = jobsCandidates.filter(
      (job) =>
        (job?.first_name || "")
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        (job?.designation || "")
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        (job?.email || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (job?.current_company || "").toString().includes(searchText) ||
        (job?.id || "").toString().includes(searchText)
    );

    const onPageChange = (event) => {
      setLazyParams({ page: event.page, rows: event.rows });
    };

    const handleSave = () => {
      const selectedCandidateName = selectedCandidateLocal?.first_name || "";
      onSelectedCandidatesChange?.(selectedCandidateLocal ? [selectedCandidateLocal] : []);
      setJobDetailsVisible(false);
    };

    const handleRefresh = () => {
      setSelectedCandidateLocal(null);
      setSearchText("");
      setLazyParams({ page: 0, rows: lazyParams.rows });
    };

    // fetch talent scan results
    const fetchTalentScanResults = async (talentScanId) => {
      setLoadingTalentScanId(talentScanId);
      try {
        const response = await axios.post(
          `/api/v1/candidate-talent-scan-result/?page=1&size=10&company_id=${companyId}`,
          { talent_scan_id: talentScanId }
        );
        if (response.status === 200) {
          setTalentScanResults(response.data.talent_scan_result.results || []);
          setShowTalentScanModal(true);
        } else {
          setTalentScanResults([]);
        }
      } catch (error) {
        console.error("Error fetching talent scan results:", error);
        setTalentScanResults([]);
      } finally {
        setLoadingTalentScanId(null);
      }
    };

    return (
      <Col lg={6}>
        <div className="field">
          <label htmlFor="job" className="block mb-0">
            {label ? label : "Candidates"}
          </label>
          <div className="d-flex align-items-center" style={{ position: "relative" }}>
            <InputText
              id="job"
              value={selectedCandidateLocal?.first_name || selectedCandidates?.[0]?.name || ""}
              onChange={(e) => { }}
              placeholder="Select Candidates"
              className="w-full"
              style={{ paddingRight: "2rem", cursor: "pointer" }}
              onClick={() => setJobDetailsVisible(true)}
              readOnly
            />
            <i
              className="pi pi-chevron-down"
              onClick={() => setJobDetailsVisible(true)}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
              }}
            ></i>
          </div>

          <Dialog
            header="Link Candidate"
            visible={jobDetailsVisible}
            onHide={() => setJobDetailsVisible(false)}
            style={{ width: "40vw" }}
            breakpoints={{ "960px": "75vw", "641px": "100vw" }}
            className="cand-details"
          >
            <Row>
              <Col lg={12}>
                <div className="p-inputgroup mb-3" style={{ position: "relative" }}>
                  <InputText
                    placeholder="Search Candidates"
                    value={searchText}
                    onInput={(e) => setSearchText(e.target.value)}
                    style={{ paddingRight: "2rem" }}
                  />
                  <i
                    className="pi pi-search"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  ></i>
                </div>
              </Col>
            </Row>

            <DataTable
              value={filteredJobs}
              selection={selectedCandidateLocal}
              onSelectionChange={(e) => {
                setSelectedCandidateLocal(e.value);
                // optionally pass selection immediately
                // onSelectedCandidatesChange?.(e.value ? [e.value] : []);
              }}
              dataKey="id"
              size="small"
              lazy
              paginator
              first={lazyParams.page * lazyParams.rows}
              rows={lazyParams.rows}
              totalRecords={totalRecordsCandidates}
              onPage={onPageChange}
              loading={loadingCandidates}
              paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              rowsPerPageOptions={[10, 20, 50, totalRecordsCandidates]}
              id="CandidateInfo"
            >
              <Column selectionMode="single" style={{ width: "3em" }} />
              <Column field="first_name" header="Candidate Name" body={(row) => row.first_name || "–"} />
              <Column field="designation" header="Designation" body={(row) => row.designation || "–"} />
              <Column field="current_company" header="Company" body={(row) => row.current_company || "–"} />
              <Column
                header="Talent Scan"
                body={(rowData) =>
                  rowData.talent_scan_id ? (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => fetchTalentScanResults(rowData.talent_scan_id)}
                      disabled={loadingTalentScanId === rowData.talent_scan_id}
                    >
                      {loadingTalentScanId === rowData.talent_scan_id && (
                        <span className="pi pi-spinner pi-spin" style={{ marginRight: "8px" }} />
                      )}
                      View Result
                    </button>
                  ) : (
                    "–"
                  )
                }
              />
            </DataTable>

            <div className="d-flex mt-3 justify-content-center">
              <button type="button" className="btn btn-primary me-2 btn-main" onClick={handleSave}>
                Save
              </button>
              <button type="button" className="btn btn-primary btn-main" onClick={handleRefresh}>
                Refresh
              </button>
            </div>
          </Dialog>

          <Dialog
            header="Talent Scan Results"
            visible={showTalentScanModal}
            onHide={() => setShowTalentScanModal(false)}
            style={{ width: "60vw" }}
            breakpoints={{ "960px": "75vw", "641px": "100vw" }}
          >
            {talentScanResults?.length > 0 ? (
              talentScanResults.map((result, index) => (
                <div key={index} className="p-3 border rounded mb-3 bg-light">
                  <h5 className="text-primary mb-3">{result?.test_info?.name || "Unnamed Test"}</h5>

                  <Row>
                    <Col md={6}>
                      <h6>🧑 Candidate Info</h6>
                      <p><strong>Name:</strong> {result?.candidate_info?.name}</p>
                      <p><strong>Email:</strong> {result?.candidate_info?.email}</p>
                      <p><strong>Phone:</strong> {result?.candidate_info?.phone}</p>
                      <p><strong>Industry:</strong> {result?.candidate_info?.industry}</p>
                      <p><strong>Experience:</strong> {result?.candidate_info?.experience} yrs</p>
                    </Col>

                    <Col md={6}>
                      <h6>🧾 Test Info</h6>
                      <p><strong>Designation:</strong> {result?.test_info?.test_designation}</p>
                      <p><strong>Skills:</strong> {result?.test_info?.skills?.join(", ")}</p>
                      <p><strong>Attempted:</strong> {result?.is_attempted ? "Yes" : "No"}</p>
                      <p><strong>Link:</strong>{" "}
                        <a href={result?.link} target="_blank" rel="noreferrer">
                          Open Talent Scan
                        </a>
                      </p>
                    </Col>
                  </Row>

                  <h6 className="mt-3">📊 Section Scores</h6>
                  <DataTable value={result?.score_info || []} responsiveLayout="scroll" size="small">
                    <Column field="section__name" header="Section" />
                    <Column field="total_score" header="Total Score" />
                    <Column field="cutoff_score" header="Cutoff" />
                    <Column field="score_secured" header="Score Secured" />
                    <Column field="percentage" header="Percentage" />
                  </DataTable>

                  <h6 className="mt-4">🏁 ATS Summary</h6>
                  <Row>
                    <Col md={4}><strong>Total Score Secured:</strong> {result?.ats_details?.total_score_secured}</Col>
                    <Col md={4}><strong>Actual Total Score:</strong> {result?.ats_details?.actual_total_score}</Col>
                    <Col md={4}><strong>Percentage:</strong> {result?.ats_details?.percentage_score}%</Col>
                  </Row>
                </div>
              ))
            ) : (
              <p>No Talent Scan results found.</p>
            )}
          </Dialog>
        </div>
      </Col>
    );
  }; // End InlineLinkCandidate

  // ========== RENDER ==========
  return (
    <>
      <div className="container-fluid p-3 outlook-calendar-page">
        <div className="outlook-page-header">
          <div>
            <h5 className="outlook-page-title">Today Interviews</h5>
            <p className="outlook-page-subtitle">
              {filteredData.length} interview{filteredData.length === 1 ? "" : "s"} scheduled for today.
            </p>
          </div>
          <div className="outlook-company-filter">
            <label>Select Company</label>
            <div style={{ width: "190px" }}>
              <CompanySelector onCompanyChange={handleCompanyChange} />
            </div>
          </div>
        </div>

        <div className="outlook-agenda-summary">
          <div className="outlook-summary-card">
            <span className="outlook-summary-icon"><FaCalendarDay /></span>
            <div>
              <div className="outlook-summary-value">{filteredData.length}</div>
              <div className="outlook-summary-label">Total Interviews</div>
            </div>
          </div>
          <div className="outlook-summary-card">
            <span className="outlook-summary-icon video"><FaVideo /></span>
            <div>
              <div className="outlook-summary-value">
                {filteredData.filter(item => String(item.event_sub_type || "").toLowerCase().includes("video")).length}
              </div>
              <div className="outlook-summary-label">Video Interviews</div>
            </div>
          </div>
          <div className="outlook-summary-card">
            <span className="outlook-summary-icon priority"><FaExclamationCircle /></span>
            <div>
              <div className="outlook-summary-value">
                {filteredData.filter(item => String(item.event_priority || "").toLowerCase() === "high").length}
              </div>
              <div className="outlook-summary-label">High Priority</div>
            </div>
          </div>
        </div>

        {/* DataTable */}
        {loading ? (
          <div className="w-100 text-center d-flex align-items-center justify-content-center" style={{ height: "80vh" }}>
            <OrgTableShimmer columns={6} rows={7} titleWidth="220px" />
          </div>
        ) : (
          <div className="card p-3 border-0 shadow-sm mb-4 outlook-agenda-card">
            <DataTable
              ref={dt}
              value={paginatedData}
              paginator
              dataKey="id"
              loading={loading}
              first={first}
              rows={rows}
              totalRecords={filteredData.length}
              id="AllEmployees_TableView"
              filters={filters}
              // filterDisplay="row"
              onFilter={onFilter}
              onPage={(e) => setFirst(e.first)}
              scrollable
              reorderableRows
              // resizableColumns
              columnResizeMode="expand"
              tableStyle={{
                minWidth: "50rem",
                borderRadius: "8px",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              }}
              // paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              // currentPageReportTemplate="{first} to {last} of {totalRecords}"
              emptyMessage="No records found."
            >
              <Column
                header="ID"
                body={(rowData, { rowIndex }) => rowIndex + 1 + first}
                style={{ width: "5%" }}
              />
               <Column
                header="Job"
                body={(row) => row.job_ids?.[0]?.name || "-"}
                // filter
                // filterField="job"
                // filterPlaceholder="Job"
              />  
              <Column
                field="subject"
                header="Subject"
                // sortable
                // filter
                // filterField="subject"
                filterPlaceholder="Subject"
              />
              <Column
                field="created_by_name"
                header="Created By"
                // sortable
                // filter
                // filterField="created_by_name"
                filterPlaceholder="Created By"
              />
              <Column
                header="Interview Date & Time"
                // filter
                // filterPlaceholder="Interview Time"
                body={(rowData) => {
                  const startTime = rowData.start_time ? formatTime(rowData.start_time) : "-";
                  const endTime = rowData.end_time ? formatTime(rowData.end_time) : "-";
                  const slots = rowData.calendit_response?.results?.data || [];
                  const interviewDate = slots[0]?.selected_date
                    ? formatInterviewDate(slots[0].selected_date)
                    : rowData.start_date
                      ? formatInterviewDate(rowData.start_date)
                      : "-";
                  return (
                    <div title={`${interviewDate} ${startTime} - ${endTime}`}>
                      <div>{interviewDate}</div>
                      <div>{startTime} - {endTime}</div>
                    </div>
                  );
                }}
              />
               <Column
                header="Meeting Link"
                // filter
                // filterPlaceholder="Meeting Link"
                body={(rowData) => {
                  const slots = rowData.calendit_response?.results?.data || [];
                  return slots.length > 0 ? (
                    <div>
                      {slots.map((slot, index) => (
                        <div key={index}>
                          {renderMeetingLinkActions(slot.participant_link || slot.meeting_link, rowData)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    renderMeetingLinkActions(rowData.participant_link || rowData.meeting_link, rowData)
                  );
                }}
              />

              <Column
                field="event_sub_type"
                header="Event Type"
                // sortable
                // filter
                // filterField="event_type"
                filterPlaceholder="Event Type"
              />
              <Column
                  field="recruiter_name"
                  header="Recruiter Name"
                  // sortable
                  // filter
                  // filterPlaceholder="Recruiter Name"
                  style={{ minWidth: "12rem" }}
                  body={(rowData) => {
                    const contactNames = rowData.contact_ids?.map(contact => contact.name) || [];
                    const displayedNames = contactNames.slice(0, 2).join(", ");
                    const allNames = contactNames.join(", ");

                    return (
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={allNames}
                      >
                        {contactNames.length > 2 ? `${displayedNames}, ...` : displayedNames}
                      </div>
                    );
                  }}

                />
              <Column
                header="Candidate"
                body={(row) => {
                  const candidateNames = getCandidateNames(row.candidate_ids);
                  return <span title={candidateNames}>{candidateNames}</span>;
                }}
                // filter
                // filterField="candidate"
                // filterPlaceholder="Candidate"
              />
             
              <Column
                field="status"
                header="Status"
                // sortable
                // filter
                // filterField="status"
                // filterPlaceholder="Status"
              />
              {/* <Column
                field="start_date"
                header="Date"
                // sortable
                // filter
                // filterField="start_date"
                // filterPlaceholder="Date"
              />
              <Column
                field="start_time"
                header="Time"
                // sortable
                // filter
                // filterField="start_time"
                // filterPlaceholder="Time"
              /> */}
              {/* <Column
                field="Duration"
                header="Duration"
                sortable
                filter
                filterField="Duration"
                filterPlaceholder="Duration"
                body={(rowData) => <span>{rowData.duration}</span>}
                className="d-none"
              /> */}
              <Column
                field="Priority"
                header="Priority"
                // sortable
                // filter
                // filterField="Priority"
                // filterPlaceholder="Priority"
                body={(rowData) => <span>{rowData.event_priority}</span>}
              />
              <Column
                header="Actions"
                body={actionTemplate}
                style={{ width: "10%", textAlign: "center" }}
              />
            </DataTable>
          </div>
        )}
      </div>

      {/* ====== ARCHIVE MODAL ====== */}
      <Modal
        isOpen={showArchiveModal}
        toggle={() => setShowArchiveModal(false)}
        centered
      >
        <ModalHeader
          toggle={() => setShowArchiveModal(false)}
          className="modal-header_box blueBtn p-3 d-flex justify-content-between align-items-center m-0"
        >
          <h6 className="m-0 text-white fw-semibold">Reason for Archive</h6>
        </ModalHeader>

        <ModalBody className="p-4">
          <div
            className="border rounded-3 p-3 shadow-sm"
            style={{ background: "#f8f9fa" }}
          >
            <Label className="fw-semibold mb-2">
              Reason for Archive <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows="4"
              placeholder="Enter the reason..."
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
            />
            {archiveReasonErr && (
              <small className="text-danger">{archiveReasonErr}</small>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3">
              <button
                className="btn blueBtn"
                onClick={() => setShowArchiveModal(false)}
                disabled={archiveSaving}
              >
                Cancel
              </button>
              <button
                className="btn blueBtn"
                onClick={handleSaveArchive}
                disabled={archiveSaving}
              >
                {archiveSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={showShareModal}
        toggle={() => setShowShareModal(false)}
        centered
      >
        <ModalHeader
          toggle={() => setShowShareModal(false)}
          className="modal-header_box blueBtn p-3 d-flex justify-content-between align-items-center m-0"
        >
          <h6 className="m-0 text-white fw-semibold">Share Meeting Link</h6>
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="mb-3">
            <h6>Select Employee/Recruiter</h6>
            <div style={{ maxHeight: 190, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 }}>
              {shareRecruiterOptions.length ? shareRecruiterOptions.map(recruiter => (
                <label key={recruiter.id} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={shareRecruiters.includes(recruiter.id)}
                    onChange={e => {
                      setShareRecruiters(prev =>
                        e.target.checked
                          ? [...prev, recruiter.id]
                          : prev.filter(id => id !== recruiter.id)
                      );
                    }}
                  />
                  {recruiter.name}
                </label>
              )) : <div className="text-muted">No recruiters found</div>}
            </div>
          </div>
          <div>
            <h6>Candidate Names</h6>
            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 }}>
              {(selectedEventForShare?.candidate_ids || []).length ? selectedEventForShare.candidate_ids.map(candidate => (
                <label key={candidate.id} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={shareCandidates.includes(candidate.id)}
                    onChange={e => {
                      setShareCandidates(prev =>
                        e.target.checked
                          ? [...prev, candidate.id]
                          : prev.filter(id => id !== candidate.id)
                      );
                    }}
                  />
                  {candidate.name}
                </label>
              )) : <div className="text-muted">No candidates found</div>}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button color="secondary" onClick={() => setShowShareModal(false)}>
            Cancel
          </Button>
          <Button color="primary" disabled={isSharing} onClick={handleSubmitShareMeeting}>
            {isSharing ? "Sharing..." : "Share"}
          </Button>
        </ModalFooter>
      </Modal>

      <Dialog
        header="Schedule Interview"
        visible={scheduleCommonPopup}
        className="interview-popup"
        style={{ width: "50vw" }}
        onHide={closeSchedulePopup}
      >
        <form>
          <p className="bg-form">
            <div className="mb-4">
              <Row className="mb-2">
                <Col xl={6}>
                  <div className="p-field flex flex-column">
                    <label htmlFor="interview">
                      Type <span className="text-danger">*</span>
                    </label>
                    <InputText disabled value={"Interview"} />
                  </div>
                </Col>
                <Col xl={6}>
                  <div className="p-field flex flex-column">
                    <label htmlFor="integer" className="block">
                      Sub-Type <span className="text-danger">*</span>
                    </label>
                    <Dropdown
                      value={scheduleSubtype}
                      onChange={(e) => {
                        setScheduleSubtype(e.value);
                        setScheduleFormErrors((prev) => ({ ...prev, event_sub_type: "" }));
                      }}
                      options={subtypeCall}
                      optionLabel="name"
                      placeholder="Subtype"
                      className="w-full search-option"
                    />
                    {scheduleFormErrors.event_sub_type && (
                      <small className="p-error">{scheduleFormErrors.event_sub_type}</small>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Dates/Times */}
              <Row className="mb-2">
                <Col xl={5}>
                  <div className="p-field flex-auto">
                    <label htmlFor="startDate" className="block">
                      Start date <span className="text-danger">*</span>
                    </label>
                    <Calendar
                      id="startDate"
                      className="w-100"
                      value={scheduleStartDate}
                      onChange={(e) => setScheduleStartdate(e.value)}
                      showIcon
                      minDate={new Date()}
                      dateFormat="yy-mm-dd"
                      icon={() => <i className="pi pi-calendar" />}
                    />
                    {scheduleFormErrors.start_date && (
                      <small className="p-error">{scheduleFormErrors.start_date}</small>
                    )}
                  </div>
                </Col>
                <Col xl={4}>
                  <div className="p-field flex-auto">
                    <label htmlFor="startTime" className="block">
                      Start Time <span className="text-danger">*</span>
                    </label>
                    <Calendar
                      id="startTime"
                      value={scheduleStartTime}
                      className="w-100"
                      onChange={(e) => setScheduleStarttime(e.value)}
                      showIcon
                      timeOnly
                      icon={() => <i className="pi pi-clock" />}
                    />
                    {scheduleFormErrors.start_time && (
                      <small className="p-error">{scheduleFormErrors.start_time}</small>
                    )}
                  </div>
                </Col>
                <Col xl={3}>
                  <div className="flex-auto">
                    <label htmlFor="endTime" className="block">
                      End Time <span className="text-danger">*</span>
                    </label>
                    <Calendar
                      id="endTime"
                      className="w-100"
                      value={scheduleEndTime}
                      onChange={(e) => setScheduleEndTime(e.value)}
                      showIcon
                      timeOnly
                      icon={() => <i className="pi pi-clock" />}
                    />
                    {scheduleFormErrors.end_time && (
                      <small className="p-error">{scheduleFormErrors.end_time}</small>
                    )}
                  </div>
                </Col>
              </Row>
              {/* <Col xl={6}>
                  <Row className="mb-2">
                    <Col xl={6}>
                      <div className="flex-auto">
                        <label htmlFor="endDate" className="block">
                          End date <span className="text-danger">*</span>
                        </label>
                        <Calendar
                          id="endDate"
                          value={scheduleEndDate}
                          onChange={(e) => setScheduleEndDate(e.value)}
                          showIcon
                          minDate={scheduleStartDate || new Date()}
                          dateFormat="yy-mm-dd"
                          icon={() => <i className="pi pi-calendar" />}
                        />
                        {scheduleFormErrors.end_date && (
                          <small className="p-error">{scheduleFormErrors.end_date}</small>
                        )}
                      </div>
                    </Col>
                    
                  </Row>
                </Col> */}
            </div>

            <div className="mb-4">
              <Row className="mb-2">
                {/* Inline LinkContactsPopup replaced here */}
                <InlineLinkContacts
                  onSelectedJobsChange={(jobs) => {
                    setSelectedScheduleJobs(jobs);
                    setSelectedScheduleCandidates([]);
                    setScheduleFormErrors((prev) => ({ ...prev, job_id: "" }));
                  }}
                  errorJob={scheduleFormErrors.job_id}
                  setSelectedScheduleContacts={(contacts) => {
                    setSelectedScheduleContacts(contacts);
                    setScheduleFormErrors((prev) => ({ ...prev, contacts: "" }));
                  }}
                  errorContact={scheduleFormErrors.contacts}
                  jobLabel={<span>Jobs</span>}
                  selectedJobs={selectedScheduleJobs}
                />
              </Row>

              <Row className="mb-2">
                {/* Inline LinkCandidate replaced here */}
                <InlineLinkCandidate
                  onSelectedCandidatesChange={(candidates) => {
                    setSelectedScheduleCandidates(candidates);
                    setScheduleFormErrors((prev) => ({ ...prev, event_sub_type: "" }));
                  }}
                  error={scheduleFormErrors.candidates}
                  jobId={selectedScheduleJobs?.[0]?.id}
                  label={<span>Candidate</span>}
                  selectedCandidates={selectedScheduleCandidates}
                />

                <Col xl={6}>
                  <div className="p-field flex flex-column">
                    <label htmlFor="username">Subject</label>
                    <InputText
                      placeholder="Subject"
                      value={scheduleSubject}
                      onChange={(e) => setScheduleSubject(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              {/* Note field removed: not used in schedule interview flow.
              <Row className="mb-2 mt-1">
                <Col xl={12}>
                  <div className="">
                    <label htmlFor="username">Note :</label>
                    <InputTextarea
                      className="w-full"
                      value={popTextares}
                      onChange={(e) => setPopTextares(e.target.value)}
                      placeholder="Interview Test"
                      rows={3}
                      cols={20}
                    />
                  </div>
                </Col>
              </Row>
              */}
            </div>

            <div>
              <Row className="mb-2">
                <Col lg={6}>
                  <Row>
                    <Col xl={6}>
                      <div className="p-field flex flex-column">
                        <label htmlFor="Priority" className="block">
                          Priority
                        </label>
                        <Dropdown
                          value={schedulePriority}
                          onChange={(e) => setSchedulePriority(e.value)}
                          options={priorityValue}
                          optionLabel="name"
                          placeholder="Priority"
                          className="w-full search-option"
                        />
                      </div>
                    </Col>
                    <Col xl={6}>
                      <div className="p-field flex flex-column d-none">
                        <label htmlFor="Duration" className="block">
                          Duration <span className="text-danger">*</span>
                        </label>
                        <Dropdown
                          value={scheduleDuration}
                          onChange={(e) => setScheduleDuration(e.value)}
                          options={[
                            { duration: "15 mins", value: 15 },
                            { duration: "30 mins", value: 30 },
                            { duration: "45 mins", value: 45 },
                            { duration: "60 mins", value: 60 }
                          ]}
                          optionLabel="duration"
                          placeholder="Duration"
                          className="w-full search-option"
                        />
                      </div>
                    </Col>

                  </Row>
                </Col>

              </Row>
            </div>

            <Row className="mb-2">
              {(selectedSchedule?.name?.toLowerCase() === "meeting" ||
                selectedSchedule?.name?.toLowerCase() === "call") && (
                  <>
                    <Col xl={4}>
                      <div className="p-field flex flex-column">
                        <label htmlFor="repeat">Repeat</label>
                        <Dropdown
                          id="repeat"
                          value={scheduleRepeat}
                          onChange={(e) => {
                            setScheduleRepeat(e.value);
                            setScheduleFormErrors((prev) => ({ ...prev, repeat: "" }));
                          }}
                          options={[
                            { label: "Do not repeat", value: "none" },
                            { label: "Daily", value: "daily" },
                            { label: "Weekly", value: "weekly" },
                            { label: "Mon-Fri", value: "mon-fri" },
                          ]}
                          optionLabel="label"
                          placeholder="Repeat"
                          className="w-full search-option"
                        />
                        {scheduleFormErrors.repeat && (
                          <small className="p-error">{scheduleFormErrors.repeat}</small>
                        )}
                      </div>
                    </Col>
                  </>
                )}
            </Row>

            <Row className="">
              <Col xl={12}>
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-primary waves-effect waves-light me-2"
                    onClick={closeSchedulePopup}
                  >
                    <i className="pi pi-times me-1"></i>
                    Cancel
                  </button>
                   <button
                    type="button"
                    className="btn btn-primary waves-effect waves-light btn btn-primary me-2 btn-main"
                    onClick={handleSaveSchedule}
                    disabled={loadingSave}
                  >
                    <i className="pi pi-save me-1"></i>
                    {loadingSave ? (
                      <span className="pi pi-spinner pi-spin" style={{ marginRight: "8px" }}></span>
                    ) : null}
                    Save
                  </button>
                </div>
              </Col>
            </Row>
          </p>
        </form>
      </Dialog>

      {/* ====== SUCCESS / ERROR POPUP ====== */}
      <SuccessErrorPopup
        show={errorAlert}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
        isSuccess={isSuccess}
      />
    </>
  );
};

export default TodaySchedule;
